import { Render } from '@/components/Helpers/ComponentRender/Render';
import { linkoutConfigs } from '@/config/routing/LinkoutConfig';
import {
  COMMUNITY_CARD_BACKGROUND,
  HOME_PRE_RNR_BACKGROUND,
  REFERAL_CARD_BACKGROUND,
} from '@/constants/images';
import { useScrollToElementOnLoad } from '@/hooks/useScrollToElementOnLoad';
import { useUserDetails } from '@/hooks/useUserDetails';
import { JourneyEducationTypes } from '@/utils/JourneyEducationTypes';
import { NotificationCenter } from '@layouts/Notification/NotificationCenter';
import { HomeActivateJourney } from '@screens/HomePage/HomeActivateJourney';
import { HomeFormulas } from '@screens/HomePage/HomeFormulas';
import { HomeGreeting } from '@screens/HomePage/HomeGreetingProps';
import { HomeTrackProgress } from '@screens/HomePage/HomeTrackProgress';
import { NutritionJourneyDeeperSection } from '@screens/NutritionPage/NutritionJourneyDeeperSection';
import { KEYS, LocalStorage } from '@utils/LocalStorage';
import { useRef } from 'react';
import { CommunityCard } from './ResultReady/CommunityCard';
import { TriSynbioticTaskCard } from './TriSynbiotic/TriSynbioticTaskCard';
import { WrappedCard } from './components/WrappedCard';
import { useWrappedCardVisibility } from './components/useWrappedCardVisibility';
import { TrustpilotReviewPopup } from './components/TrustpilotReviewPopup';

export const HomePage = () => {
  const userDetails = useUserDetails();
  const { firstName } = userDetails;
  const localStorageUser = LocalStorage.getItem(KEYS.BOOTUP);
  const { isATSUser = false } = localStorageUser
    ? JSON.parse(localStorageUser)
    : {};
  const { visible: showWrappedCard } = useWrappedCardVisibility();
  const communitySectionRef = useRef<HTMLDivElement>(null);

  const NAVBAR_OFFSET = 70;

  useScrollToElementOnLoad({
    targetId: 'community',
    targetRef: communitySectionRef,
    loadingStates: [],
    scrollOffset: NAVBAR_OFFSET,
  });

  return (
    <div className="flex flex-col">
      <TrustpilotReviewPopup />
      <div
        className="relative"
        style={{
          backgroundImage: `url(${HOME_PRE_RNR_BACKGROUND})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="page-container">
          <div className="flex justify-between">
            <Render when={firstName}>
              <HomeGreeting userName={firstName} />
            </Render>
            <div className="mr-6 md:mr-0">
              <NotificationCenter screen="nornr" />
            </div>
          </div>
          <HomeActivateJourney screen="nornr" />
        </div>
      </div>

      <div className="bg-[#E4ECF4]  flex-1">
        <div className=" flex flex-col gap-6 page-container">
          {isATSUser && <TriSynbioticTaskCard />}
          {showWrappedCard && <WrappedCard />}
          <HomeTrackProgress screen="nornr" />
          <NutritionJourneyDeeperSection
            data-testid="home-testingResourceSection"
            sectionInfo={JourneyEducationTypes.HOME_PRE_RNR_TESTING_RESOURCE}
            screen="home"
          />
          <HomeFormulas />
          <NutritionJourneyDeeperSection
            data-testid="home-preRnrSection"
            sectionInfo={JourneyEducationTypes.HOME_PRE_RNR}
            screen="home"
          />
        </div>
        <div ref={communitySectionRef}>
          <section className="relative transition-all duration-500">
            <div className="mt-12 page-container">
              <div className="flex flex-col lg:flex-row justify-center gap-2 pb-20">
                <CommunityCard
                  data-testid="home-joinCommunityCard"
                  backgroundImage={COMMUNITY_CARD_BACKGROUND}
                  title="Join the Community"
                  description="Get connected and find your fellow wellness enthusiasts in our private, online community."
                  buttonText="Explore"
                  externalLink={linkoutConfigs.community}
                  eventName="home_join_the_community"
                />
                <CommunityCard
                  data-testid="home-referFriendCard"
                  backgroundImage={REFERAL_CARD_BACKGROUND}
                  title="Give $50, Get $50"
                  description="When you refer a friend to join the Viome community, you'll both benefit."
                  buttonText="Refer a friend"
                  externalLink={linkoutConfigs.referAFriend}
                  eventName="home_refer_a_friend"
                />
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};
